import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useLanguage } from '../lib/language'
import toast from 'react-hot-toast'

export default function LoginPage() {
  const { signIn } = useAuth()
  const { t } = useLanguage()
  const navigate = useNavigate()
  const [form,         setForm]         = useState({ email: '', password: '' })
  const [loading,      setLoading]      = useState(false)
  const [error,        setError]        = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showForgot,   setShowForgot]   = useState(false)
  const [resetEmail,   setResetEmail]   = useState('')
  const [resetSent,    setResetSent]    = useState(false)
  const [resetLoading, setResetLoading] = useState(false)
  const [mfaRequired,  setMfaRequired]  = useState(false)
  const [mfaCode,      setMfaCode]      = useState('')
  const [mfaFactorId,  setMfaFactorId]  = useState(null)
  const [mfaChallengeId, setMfaChallengeId] = useState(null)
  const [mfaLoading,   setMfaLoading]   = useState(false)

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const { supabase } = await import('../lib/supabase')
      const { data, error } = await supabase.auth.signInWithPassword({ email: form.email, password: form.password })
      if (error) throw error

      // ✅ Check if MFA is required!!
      if (data?.session?.mfa_required || data?.weakSession) {
        const { data: factorsData } = await supabase.auth.mfa.listFactors()
        const totpFactor = factorsData?.totp?.[0]
        if (totpFactor) {
          const { data: challengeData, error: challengeErr } = await supabase.auth.mfa.challenge({ factorId: totpFactor.id })
          if (challengeErr) throw challengeErr
          setMfaFactorId(totpFactor.id)
          setMfaChallengeId(challengeData.id)
          setMfaRequired(true)
          setLoading(false)
          return
        }
      }
      navigate('/portal')
    } catch (err) {
      setError(err.message || 'Invalid email or password')
    } finally {
      setLoading(false)
    }
  }

  async function handleMfaVerify(e) {
    e.preventDefault()
    setMfaLoading(true)
    setError('')
    try {
      const { supabase } = await import('../lib/supabase')
      const { error } = await supabase.auth.mfa.verify({
        factorId:    mfaFactorId,
        challengeId: mfaChallengeId,
        code:        mfaCode.trim(),
      })
      if (error) throw error
      navigate('/portal')
    } catch (err) {
      setError('Invalid verification code — please try again!!')
    } finally {
      setMfaLoading(false)
    }
  }

  async function handleReset(e) {
    e.preventDefault()
    if (!resetEmail) return setError('Please enter your email address')
    setResetLoading(true)
    setError('')
    try {
      const { error } = await import('../lib/supabase').then(m => 
        m.supabase.auth.resetPasswordForEmail(resetEmail, {
          redirectTo: `${window.location.origin}/reset-password`,
        })
      )
      if (error) throw error
      setResetSent(true)
    } catch (err) {
      setError(err.message || 'Could not send reset email. Please try again.')
    } finally {
      setResetLoading(false)
    }
  }

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
    :focus-visible { outline: 2px solid #4D4E8D; outline-offset: 3px; border-radius: 6px; }

    .lca-skip-link { position:absolute; top:-100px; left:16px; background:#4D4E8D; color:#fff; padding:8px 16px; border-radius:8px; font-size:13px; font-weight:600; text-decoration:none; z-index:9999; transition:top .2s; font-family:'Plus Jakarta Sans',sans-serif; }
    .lca-skip-link:focus { top:16px; }

    .lca-input { width:100%; padding:12px 14px; border:1.5px solid #D7DADE; border-radius:10px; font-size:16px; color:#151715; background:#fff; transition:border-color .2s, box-shadow .2s; display:block; font-family:'Plus Jakarta Sans',sans-serif; }
    .lca-input:focus { border-color:#4D4E8D; box-shadow:0 0 0 3px rgba(77,78,141,0.12); outline:none; background:#fff; }
    .lca-input::placeholder { color:#A2A6AA; }
    .lca-input[aria-invalid="true"] { border-color:#C0392B; box-shadow:0 0 0 3px rgba(192,57,43,0.1); }

    .lca-label { display:block; font-size:13px; font-weight:600; color:#5C6065; margin-bottom:6px; letter-spacing:.01em; font-family:'Plus Jakarta Sans',sans-serif; }

    .lca-btn-submit { width:100%; padding:13px 20px; background:#4D4E8D; color:#fff; border:none; border-radius:11px; font-size:15px; font-weight:700; cursor:pointer; transition:background .2s, transform .15s; font-family:'Plus Jakarta Sans',sans-serif; }
    .lca-btn-submit:hover:not(:disabled) { background:#41427a; transform:translateY(-1px); }
    .lca-btn-submit:disabled { opacity:.6; cursor:not-allowed; }

    .lca-error { background:#FBEAE8; border:1px solid #E7B0A8; border-radius:10px; padding:10px 14px; font-size:13px; color:#B23A2E; margin-bottom:18px; display:flex; align-items:center; gap:8px; }

    .lca-bottom-links { margin-top:22px; display:flex; flex-wrap:wrap; justify-content:center; gap:8px 16px; font-size:12px; color:#8A8F94; text-align:center; padding:0 16px; font-family:'Plus Jakarta Sans',sans-serif; }
    .lca-bottom-links a { color:#5C6065; text-decoration:none; font-weight:500; transition:color .2s; }
    .lca-bottom-links a:hover { color:#4D4E8D; }

    .lca-card-link { color:#4D4E8D; font-weight:700; text-decoration:none; }
    .lca-card-link:hover { text-decoration:underline; }

    @media (max-width: 480px) { .lca-card { padding:24px 18px !important; } }
  `

  // ✅ MFA verification screen!!
  if (mfaRequired) {
    return (
      <div style={{ minHeight:'100vh', background:'#E9EAEC', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'24px 16px' }}>
        <div style={{ background:'#fff', borderRadius:20, padding:40, width:'100%', maxWidth:400, textAlign:'center', border:'1px solid #E2E4E7', boxShadow:'0 18px 44px rgba(21,23,21,0.10)' }}>
          <div style={{ fontSize:48, marginBottom:16 }}>🔐</div>
          <div style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:24, fontWeight:600, color:'#151715', marginBottom:8 }}>Two-Factor Authentication</div>
          <div style={{ fontSize:13, color:'#7A7870', marginBottom:24 }}>Open your authenticator app and enter the 6-digit code!!</div>
          {error && <div style={{ background:'#FDF2F2', color:'#C0392B', padding:'10px 14px', borderRadius:8, fontSize:13, marginBottom:16 }}>{error}</div>}
          <form onSubmit={handleMfaVerify}>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={mfaCode}
              onChange={e => setMfaCode(e.target.value.replace(/\D/g,''))}
              placeholder="000000"
              style={{ width:'100%', padding:'14px', border:'2px solid #D7DADE', borderRadius:10, fontSize:24, textAlign:'center', letterSpacing:'0.3em', fontFamily:'monospace', marginBottom:16 }}
              autoFocus
            />
            <button type="submit" disabled={mfaLoading || mfaCode.length !== 6} className="lca-btn-submit" style={{ width:'100%' }}>
              {mfaLoading ? 'Verifying...' : 'Verify & Sign In →'}
            </button>
          </form>
          <button onClick={() => { setMfaRequired(false); setMfaCode(''); setError('') }} style={{ marginTop:16, background:'none', border:'none', color:'#7A7870', fontSize:12, cursor:'pointer' }}>
            ← Back to login
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#E9EAEC',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px 16px',
    }}>
      <style>{styles}</style>

      {/* ADA: Skip to main content */}
      <a href="#login-form" className="lca-skip-link">Skip to login form</a>

      {/* LOGO */}
      <Link
        to="/"
        style={{ textDecoration: 'none', marginBottom: 28, textAlign: 'center' }}
        aria-label="Notary Signing Vault, LLC — Go to homepage"
      >
        <img
          src="/nsv_emblem.png?v=2"
          alt="Notary Signing Vault, LLC — Nationwide Notary Professionals Network"
          style={{
            height: 84,
            width: 'auto',
            objectFit: 'contain',
            marginBottom: 8,
            filter: 'drop-shadow(0 4px 16px rgba(77,78,141,0.18))',
          }}
          onError={e => {
            e.target.style.display = 'none'
            e.target.nextSibling.style.display = 'block'
          }}
        />
        {/* Fallback text if logo fails to load */}
        <div style={{ display: 'block', marginTop: 4 }} aria-hidden="true">
          <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 20, color: '#151715', fontWeight: 800 }}>
            Notary Signing Vault, LLC
          </div>
          <div style={{ fontSize: 10, color: '#4D4E8D', letterSpacing: '0.1em', marginTop: 2 }}>
            NATIONWIDE NOTARY PROFESSIONALS NETWORK
          </div>
        </div>
      </Link>

      {/* LOGIN CARD */}
      <div
        className="lca-card"
        style={{
          background: '#fff',
          borderRadius: 20,
          padding: '32px 28px',
          width: '100%',
          maxWidth: 400,
          boxShadow: '0 18px 44px rgba(21,23,21,0.10)',
        }}
      >
        {/* ADA: h1 as page title, not just visual heading */}
        <h1 style={{
          fontSize: 24,
          marginBottom: 4,
          color: '#151715',
          fontFamily: 'Plus Jakarta Sans, sans-serif',
          fontWeight: 700,
        }}>
          {t('login_welcome')}
        </h1>
        <p style={{ fontSize: 13, color: '#7A7870', marginBottom: 24, lineHeight: 1.6 }}>
          {t('login_sub')}
        </p>

        {/* ADA: aria-live so screen readers announce errors immediately */}
        {error && (
          <div
            className="lca-error"
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
          >
            {/* ADA: aria-hidden on decorative icon */}
            <span aria-hidden="true">⚠️</span>
            <span>{error}</span>
          </div>
        )}

        {/* ADA: id on form so skip link targets it */}
        <form id="login-form" onSubmit={handleSubmit} noValidate aria-label="Sign in form">

          {/* EMAIL FIELD */}
          <div style={{ marginBottom: 16 }}>
            <label className="lca-label" htmlFor="email">
              {t('login_email')}
              {/* ADA: required indicator with explanation */}
              <span aria-hidden="true" style={{ color: '#C0392B', marginLeft: 3 }}>*</span>
            </label>
            <input
              id="email"
              className="lca-input"
              type="email"
              placeholder="you@example.com"
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              required
              autoComplete="email"
              aria-required="true"
              aria-invalid={error ? 'true' : 'false'}
              aria-describedby={error ? 'login-error' : undefined}
            />
          </div>

          {/* PASSWORD FIELD */}
          <div style={{ marginBottom: 24, position:'relative' }}>
            <label className="lca-label" htmlFor="password">
              {t('login_password')}
              <span aria-hidden="true" style={{ color: '#C0392B', marginLeft: 3 }}>*</span>
            </label>
            <input
              id="password"
              className="lca-input"
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              required
              autoComplete="current-password"
              aria-required="true"
              aria-invalid={error ? 'true' : 'false'}
              style={{ paddingRight: 40 }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(s => !s)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'#7A7870', fontSize:16, padding:4 }}
            >
              {showPassword ? '🙈' : '👁️'}
            </button>
          </div>

          {/* Forgot password link */}
          <div style={{ textAlign: 'right', marginTop: -8, marginBottom: 12 }}>
            <button
              type="button"
              onClick={() => { setShowForgot(true); setError('') }}
              style={{ background: 'none', border: 'none', color: '#4D4E8D', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}
            >
              Forgot your password?
            </button>
          </div>

          {/* SUBMIT BUTTON */}
          <button
            className="lca-btn-submit"
            type="submit"
            disabled={loading}
            aria-busy={loading}
            aria-label={loading ? 'Signing in, please wait…' : t('login_btn')}
          >
            {loading ? (
              <span>
                <span aria-hidden="true">⏳ </span>
                {t('login_loading')}
              </span>
            ) : t('login_btn')}
          </button>
        </form>

        {/* REGISTER LINK */}
        <div style={{ marginTop: 20, textAlign: 'center', fontSize: 13, color: '#7A7870' }}>
          {t('login_no_account')}{' '}
          <Link to="/register" className="lca-card-link">
            {t('login_register')}
          </Link>
        </div>

        {/* FORGOT PASSWORD MODAL */}
        {showForgot && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, padding: 16,
          }}>
            <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 400 }}>
              {!resetSent ? (
                <>
                  <h2 style={{ fontSize: 20, color: '#151715', fontFamily: 'Plus Jakarta Sans, sans-serif', marginBottom: 8 }}>Reset your password</h2>
                  <p style={{ fontSize: 13, color: '#7A7870', marginBottom: 20 }}>Enter your email and we'll send you a reset link!</p>
                  {error && (
                    <div style={{ background: '#FDF2F2', border: '1px solid #F5C6C6', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#C0392B', marginBottom: 14 }}>
                      {error}
                    </div>
                  )}
                  <form onSubmit={handleReset} noValidate>
                    <div className="form-group" style={{ marginBottom: 16 }}>
                      <label className="lca-label" htmlFor="reset-email">Email address</label>
                      <input
                        id="reset-email"
                        type="email"
                        className="lca-input"
                        placeholder="you@email.com"
                        value={resetEmail}
                        onChange={e => setResetEmail(e.target.value)}
                        required
                        autoFocus
                      />
                    </div>
                    <button
                      type="submit"
                      className="lca-btn-submit"
                      disabled={resetLoading}
                      style={{ marginBottom: 10 }}
                    >
                      {resetLoading ? 'Sending...' : 'Send Reset Link →'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowForgot(false); setError('') }}
                      style={{ width: '100%', background: 'none', border: 'none', color: '#7A7870', fontSize: 13, cursor: 'pointer', padding: 8 }}
                    >
                      Cancel
                    </button>
                  </form>
                </>
              ) : (
                <>
                  <div style={{ textAlign: 'center', padding: '8px 0 20px' }}>
                    <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#EAF5EE', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 24 }}>✓</div>
                    <h2 style={{ fontSize: 20, color: '#151715', fontFamily: 'Plus Jakarta Sans, sans-serif', marginBottom: 8 }}>Check your email!</h2>
                    <p style={{ fontSize: 13, color: '#7A7870', lineHeight: 1.7, marginBottom: 20 }}>
                      We sent a password reset link to <strong style={{ color: '#151715' }}>{resetEmail}</strong>.<br />
                      Check your inbox and click the link to reset your password!
                    </p>
                    <button
                      type="button"
                      className="lca-btn-submit"
                      onClick={() => { setShowForgot(false); setResetSent(false); setResetEmail('') }}
                    >
                      Back to Sign In
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* BOTTOM PORTAL LINKS */}
      {/* ADA: nav landmark for bottom links */}
      <nav className="lca-bottom-links" aria-label="Portal registration links">
        <Link to="/register?role=notary">{t('login_notary')}</Link>
        <span aria-hidden="true">·</span>
        <Link to="/register?role=client">{t('login_client')}</Link>
        <span aria-hidden="true">·</span>
        <Link to="/register?role=individual">{t('login_individual')}</Link>
      </nav>

    </div>
  )
}
